/**
 * "Connect WhatsApp" - the code-and-confirm sheet.
 *
 * One code, three screens: send it, confirm it is you, connected. The server
 * owns every state change; this modal only asks for a code, polls, and
 * confirms. An inbound message is never a connection by itself - the person
 * taps Connect here, after seeing the masked number that wrote in.
 *
 * Every timer (poll, countdown tick, auto-close) is owned by an effect keyed
 * on `open`, so closing or unmounting mid-flow clears them all. Nothing here
 * ever reads as connected unless the server said `confirmed`.
 *
 * @module web/screens/app/overview/WhatsAppConnectModal
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, get, post } from "../../../api/client.ts";
import type { LinkIntentConfirmResponse, LinkIntentResponse, LinkIntentStatusResponse } from "../../../api/types.ts";
import { PRIMARY_LABEL, errorSentence, formatCountdown, intentView, nextPollDelay } from "./customerChannels.ts";

const AUTO_CLOSE_MS = 1200;

/** One sentence for whatever a request threw. */
function describe(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { code?: unknown; error?: unknown } | null;
    const code = body && typeof body.code === "string" ? body.code : null;
    const fallback = body && typeof body.error === "string" ? body.error : e.message;
    return errorSentence(e.status, code, fallback);
  }
  return errorSentence(0, null, e instanceof Error ? e.message : null);
}

export function WhatsAppConnectModal({ open, brandId, onClose, onConnected }: {
  open: boolean;
  brandId: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  /** The code and deep link, shown once. */
  const [intent, setIntent] = useState<LinkIntentResponse | null>(null);
  /** What the server last said about it. */
  const [status, setStatus] = useState<LinkIntentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  /** Bumped by "New code", so the create effect runs again. */
  const [attempt, setAttempt] = useState(0);
  const onConnectedRef = useRef(onConnected);
  const onCloseRef = useRef(onClose);
  onConnectedRef.current = onConnected;
  onCloseRef.current = onClose;

  // 1. Open (or "New code") → ask for a code. Closing forgets everything.
  useEffect(() => {
    if (!open) {
      setIntent(null); setStatus(null); setError(null); setBusy(false);
      return;
    }
    let live = true;
    setIntent(null); setStatus(null); setError(null);
    void post<LinkIntentResponse>("/api/customer-channels/link-intents", { provider: "whatsapp", brandId })
      .then(d => {
        if (!live) return;
        setIntent(d);
        setStatus({ id: d.id, provider: d.provider, status: "issued", senderMasked: null, expiresAt: d.expiresAt });
        setNow(Date.now());
      })
      .catch(e => { if (live) setError(describe(e)); });
    return () => { live = false; };
  }, [open, brandId, attempt]);

  // 2. Poll while the code can still change state. `tick` re-arms the timer
  //    after every answer, so one failed poll does not end the polling.
  const intentId = status?.id ?? null;
  const statusKind = status?.status ?? null;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open || !intentId) return;
    const delay = nextPollDelay(statusKind);
    if (delay === null) return;
    let live = true;
    const t = window.setTimeout(() => {
      void get<LinkIntentStatusResponse>(`/api/customer-channels/link-intents/${intentId}`)
        .then(d => { if (live) { setStatus(d); setError(null); } })
        .catch(e => {
          if (!live) return;
          setError(describe(e));
          // ponytail: 401/403/404 will not heal by polling — the intent is gone
          // or the person is. Park it as canceled so "New code" is the way out.
          if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
            setStatus(s => (s ? { ...s, status: "canceled" } : s));
          }
        })
        .finally(() => { if (live) setTick(n => n + 1); });
    }, delay);
    return () => { live = false; window.clearTimeout(t); };
  }, [open, intentId, statusKind, tick]);

  // 3. Countdown ticks once a second while the code is unsent.
  useEffect(() => {
    if (!open || statusKind !== "issued") return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [open, statusKind]);

  // 4. Confirmed → let it sink in, then hand back to the row.
  useEffect(() => {
    if (!open || statusKind !== "confirmed") return;
    const t = window.setTimeout(() => { onConnectedRef.current(); onCloseRef.current(); }, AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [open, statusKind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const confirm = useCallback(async () => {
    if (!intentId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const d = await post<LinkIntentConfirmResponse>(`/api/customer-channels/link-intents/${intentId}/confirm`);
      // Only the server's word counts. The connection in the answer is the proof.
      if (d?.connection?.status === "active") {
        setStatus(s => s ? { ...s, status: "confirmed", senderMasked: d.connection.senderMasked } : s);
      } else {
        setError(errorSentence(0, null, null));
      }
    } catch (e) {
      setError(describe(e));
      // A 409 means the intent moved on without us; the next poll says where.
      if (e instanceof ApiError && e.status === 409) {
        void get<LinkIntentStatusResponse>(`/api/customer-channels/link-intents/${intentId}`)
          .then(d => setStatus(d))
          .catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  }, [intentId, busy]);

  if (!open) return null;

  const view = intentView(status, error);
  const showCode = !!intent && statusKind === "issued";
  const done = statusKind === "confirmed";

  return (
    <div
      className="pm-modal-backdrop"
      id="wa-modal-backdrop"
      style={{ display: "flex" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pm-modal glass" style={{ maxWidth: 480 }} role="dialog" aria-labelledby="wa-modal-title">
        <button className="pm-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        <h2 className="pm-modal-title" id="wa-modal-title" style={done ? { color: "var(--success)" } : undefined}>
          {view.title}
        </h2>
        <p className="pm-modal-sub" style={error ? { color: "#fca5a5", marginBottom: 20 } : { marginBottom: 20 }} aria-live="polite">
          {view.body}
          {showCode && !error && <> Code expires in <strong style={{ fontVariantNumeric: "tabular-nums" }}>{formatCountdown(intent.expiresAt, now)}</strong>.</>}
        </p>

        {showCode && (
          <div
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 34, fontWeight: 700, letterSpacing: "0.18em",
              textAlign: "center", padding: "18px 12px", marginBottom: 20,
              borderRadius: 14, border: "1px solid var(--glass-border)",
              background: "rgba(255,255,255,0.04)", color: "var(--ink-1)",
              userSelect: "all",
            }}
            aria-label="Your connect code"
          >
            {intent.code}
          </div>
        )}

        {/*
          The opt-in sentence. It sits on the CONFIRM step because confirming is
          what gets recorded (time + wording version) in `channel_connections` —
          linking alone is not consent to be messaged.
        */}
        {view.primary === "confirm" && (
          <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--ink-3)", margin: "0 0 18px" }}>
            By connecting you agree that Aditor may message this number on WhatsApp when a video is
            finished. Reply STOP any time to end it, or disconnect here.
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {view.secondary === "cancel" && (
            <button className="integration-action" onClick={onClose} disabled={busy}>No, cancel</button>
          )}
          {view.primary === "open" && intent && (
            <a
              className="integration-action primary"
              href={intent.deepLink}
              target="_blank"
              rel="noopener"
              style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
            >
              {PRIMARY_LABEL.open}
            </a>
          )}
          {view.primary === "confirm" && (
            <button className="integration-action primary" onClick={() => void confirm()} disabled={busy}>
              {busy ? "Connecting…" : PRIMARY_LABEL.confirm}
            </button>
          )}
          {view.primary === "new_code" && (
            <button className="integration-action primary" onClick={() => setAttempt(a => a + 1)}>
              {PRIMARY_LABEL.new_code}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
